/**
 * Nosto-ProductCarousel.js
 *
 * Carrusel general de productos.
 *
 * Responsabilidades:
 * - Escuchar recomendaciones de Nosto.
 * - Renderizar collection tabs ya generados por Liquid.
 * - Solicitar product.product-card por AJAX para recomendaciones Nosto.
 * - Insertar <product-card> en el orden entregado por Nosto.
 * - Conservar contexto de atribución de Nosto en el DOM.
 * - Inicializar/refrescar ProductCard.js.
 * - Reinicializar el grid-slider externo del theme.
 * - Grid colapsable con "Ver más" ([data-nosto-grid-reveal]).
 *
 * ProductCard.js es el único responsable del comportamiento
 * interno de cada product card.
 */

(() => {
  "use strict";

  class NostoProductCarousel extends HTMLElement {
    constructor() {
      super();

      this.destroyed = false;

      this.recommendedProducts = [];

      this.handleRecommendationsLoaded =
        this.handleRecommendationsLoaded.bind(this);

      this.handleRecommendationsError =
        this.handleRecommendationsError.bind(this);

      this.handleSectionLoad =
        this.handleSectionLoad.bind(this);

      this.handleTabClick =
        this.handleTabClick.bind(this);
    }


    /* ==========================================================================
       Lifecycle
       ========================================================================== */

    connectedCallback() {
      this.destroyed = false;

      this.section =
        this.closest(
          ".nosto-product-carousel"
        );

      if (!this.section) {
        console.warn(
          "[NostoProductCarousel] No se encontró la sección."
        );

        return;
      }

      try {
        this.productViewService =
          this.createProductViewService();

        /*
         * Compartimos una única referencia global.
         *
         * Quick Buy también puede reutilizar este service.
         */
        window.NOSTO_PRODUCT_VIEW_SERVICE =
          window.NOSTO_PRODUCT_VIEW_SERVICE ||
          this.productViewService;

        this.initializeQuickBuyModal();
      } catch (error) {
        console.error(
          "[NostoProductCarousel] No se pudo crear ProductViewService.",
          error
        );

        return;
      }


      /*
       * Registramos eventos antes de inicializar el panel activo.
       *
       * Nosto puede responder de manera asíncrona.
       */
      this.addEventListener(
        "nosto:recommendations:loaded",
        this.handleRecommendationsLoaded
      );

      this.addEventListener(
        "nosto:recommendations:error",
        this.handleRecommendationsError
      );

      this.addEventListener(
        "click",
        this.handleTabClick
      );

      document.addEventListener(
        "shopify:section:load",
        this.handleSectionLoad
      );


      this.initializeGridReveals();


      requestAnimationFrame(
        async () => {
          const activePanel =
            this.querySelector(
              "[data-nosto-carousel-panel].is-active"
            );

          if (!activePanel) {
            return;
          }

          await this.initializePanel(
            activePanel
          );
        }
      );
    }


    disconnectedCallback() {
      this.destroyed = true;

      this.removeEventListener(
        "nosto:recommendations:loaded",
        this.handleRecommendationsLoaded
      );

      this.removeEventListener(
        "nosto:recommendations:error",
        this.handleRecommendationsError
      );

      this.removeEventListener(
        "click",
        this.handleTabClick
      );

      document.removeEventListener(
        "shopify:section:load",
        this.handleSectionLoad
      );

      this.destroyGridReveals();
    }


    /* ==========================================================================
       Grid reveal
       ========================================================================== */

    /*
     * Cada panel muestra solo el primer renglón y un asomo del
     * segundo. La altura colapsada se recalcula con ResizeObserver
     * porque los cards de Nosto llegan por AJAX, el panel puede
     * estar oculto (tabs) y el número de columnas cambia por breakpoint.
     */
    initializeGridReveals() {
      this.destroyGridReveals();

      this.gridRevealCleanups = [];

      this.querySelectorAll(
        "[data-nosto-grid-reveal]"
      ).forEach((reveal) => {
        const viewport =
          reveal.querySelector(
            "[data-nosto-grid-reveal-viewport]"
          );

        const track =
          reveal.querySelector(
            "[data-nosto-product-carousel-track]"
          );

        const button =
          reveal.querySelector(
            "[data-nosto-grid-reveal-button]"
          );

        if (!viewport || !track || !button) {
          return;
        }

        const update = () => {
          this.updateGridReveal(
            reveal,
            viewport,
            track
          );
        };

        const handleClick = () => {
          this.expandGridReveal(
            reveal,
            viewport,
            button
          );
        };

        /*
         * Si el teclado entra a un producto oculto,
         * expandimos para que no quede recortado.
         */
        const handleFocusIn = () => {
          if (
            reveal.dataset.state ===
            "collapsed"
          ) {
            handleClick();
          }
        };

        const observer =
          new ResizeObserver(update);

        observer.observe(track);

        button.addEventListener(
          "click",
          handleClick
        );

        viewport.addEventListener(
          "focusin",
          handleFocusIn
        );

        update();

        this.gridRevealCleanups.push(() => {
          observer.disconnect();

          button.removeEventListener(
            "click",
            handleClick
          );

          viewport.removeEventListener(
            "focusin",
            handleFocusIn
          );
        });
      });
    }


    destroyGridReveals() {
      this.gridRevealCleanups?.forEach(
        (cleanup) => cleanup()
      );

      this.gridRevealCleanups = [];
    }


    updateGridReveal(
      reveal,
      viewport,
      track
    ) {
      const state =
        reveal.dataset.state;

      if (
        state === "expanding" ||
        state === "expanded"
      ) {
        return;
      }

      const firstItem =
        track.firstElementChild;

      /*
       * Panel oculto o todavía sin contenido.
       */
      if (
        !firstItem ||
        track.clientHeight <= 0
      ) {
        return;
      }

      const styles =
        getComputedStyle(reveal);

      const peek =
        Number.parseFloat(
          styles.getPropertyValue(
            "--nosto-grid-reveal-peek"
          )
        ) || 0;

      const rowGap =
        Number.parseFloat(
          getComputedStyle(track).rowGap
        ) || 0;

      const firstRowBottom =
        firstItem.getBoundingClientRect()
          .bottom -
        viewport.getBoundingClientRect()
          .top;

      const collapsedHeight =
        Math.ceil(
          firstRowBottom +
          rowGap +
          peek
        );

      /*
       * Con un solo renglón no hay nada que revelar.
       */
      const hasMore =
        viewport.scrollHeight >
        firstRowBottom + rowGap + 1;

      reveal.dataset.state =
        hasMore
          ? "collapsed"
          : "static";

      reveal.style.setProperty(
        "--nosto-grid-reveal-collapsed-height",
        `${collapsedHeight}px`
      );
    }


    expandGridReveal(
      reveal,
      viewport,
      button
    ) {
      if (
        reveal.dataset.state !==
        "collapsed"
      ) {
        return;
      }

      reveal.dataset.state =
        "expanding";

      button.setAttribute(
        "aria-expanded",
        "true"
      );

      /*
       * Animamos max-height hasta la altura real del grid.
       */
      reveal.style.setProperty(
        "--nosto-grid-reveal-collapsed-height",
        `${viewport.scrollHeight}px`
      );

      let finished = false;

      const finish = () => {
        if (finished) {
          return;
        }

        finished = true;

        viewport.removeEventListener(
          "transitionend",
          handleTransitionEnd
        );

        /*
         * Sin límite de altura para que swatches,
         * resize o cards nuevos no queden recortados.
         */
        reveal.dataset.state =
          "expanded";
      };

      const handleTransitionEnd = (
        event
      ) => {
        if (
          event.target === viewport &&
          event.propertyName ===
            "max-height"
        ) {
          finish();
        }
      };

      viewport.addEventListener(
        "transitionend",
        handleTransitionEnd
      );

      const duration =
        Number.parseFloat(
          getComputedStyle(reveal)
            .getPropertyValue(
              "--nosto-grid-reveal-duration"
            )
        ) || 0.8;

      setTimeout(
        finish,
        duration * 1000 + 100
      );
    }


    /* ==========================================================================
       Product View Service
       ========================================================================== */

    createProductViewService() {
      if (
        typeof window.NostoProductViewService !==
        "function"
      ) {
        throw new Error(
          "NostoProductViewService no está disponible."
        );
      }

      return new window.NostoProductViewService({
        cardView: "product-card",

        quickBuyView:
          "nosto-quick-buy",

        concurrency: 4,

        cache: true,

        debug:
          window.NOSTO_DEBUG === true,
      });
    }


    /* ==========================================================================
       Quick Buy
       ========================================================================== */

    initializeQuickBuyModal() {
      /*
       * Una única instancia global.
       *
       * Puede haber varios carruseles en una página.
       */
      if (
        window.NOSTO_QUICK_BUY_MODAL
          ?.isInitialized
      ) {
        return window
          .NOSTO_QUICK_BUY_MODAL;
      }

      if (
        typeof window.NostoQuickBuyModal !==
        "function"
      ) {
        console.warn(
          "[NostoProductCarousel] NostoQuickBuyModal no está disponible."
        );

        return null;
      }

      const modalElement =
        document.querySelector(
          "[data-nosto-quick-buy-modal]"
        );

      if (!modalElement) {
        console.warn(
          "[NostoProductCarousel] No existe el modal global de quick buy."
        );

        return null;
      }

      try {
        const controller =
          new window.NostoQuickBuyModal({
            modal:
              modalElement,

            viewService:
              window
                .NOSTO_PRODUCT_VIEW_SERVICE,

            debug:
              window.NOSTO_DEBUG === true,
          });

        controller.init();

        window.NOSTO_QUICK_BUY_MODAL =
          controller;

        return controller;
      } catch (error) {
        console.error(
          "[NostoProductCarousel] No fue posible inicializar el quick buy.",
          error
        );

        return null;
      }
    }


    /* ==========================================================================
       Shopify Theme Editor
       ========================================================================== */

    async handleSectionLoad(
      event
    ) {
      const shopifySection =
        this.closest(
          ".shopify-section"
        );

      if (
        event.target !==
          shopifySection &&
        !event.target.contains(this)
      ) {
        return;
      }

      const activePanel =
        this.querySelector(
          "[data-nosto-carousel-panel].is-active"
        );

      if (!activePanel) {
        return;
      }

      await this.initializePanel(
        activePanel
      );
    }


    /* ==========================================================================
       Collection panels
       ========================================================================== */

    initializeCollectionPanels() {
      const activePanel =
        this.querySelector(
          '[data-nosto-carousel-panel].is-active[data-source-type="collection_tab"]'
        );

      if (!activePanel) {
        return;
      }

      this.renderCollectionPanel(
        activePanel
      );
    }


    renderCollectionPanel(
      panel
    ) {
      if (
        !panel ||
        panel.hidden ||
        !panel.classList.contains(
          "is-active"
        )
      ) {
        return;
      }

      if (
        panel.dataset.rendered ===
        "true"
      ) {
        return;
      }

      /*
       * Collection tabs ya contienen
       * <product-card> generado por Liquid.
       */
      const cards =
        panel.querySelectorAll(
          "[data-product-card]"
        );

      if (!cards.length) {
        this.setPanelEmpty(
          panel
        );

        return;
      }

      panel.dataset.rendered =
        "true";


      /*
       * ProductCard.js controla toda
       * la interacción interna.
       */
      if (
        typeof window
          .initializeProductCards ===
        "function"
      ) {
        window.initializeProductCards(
          panel
        );
      }
    }


    /* ==========================================================================
       Panel initialization
       ========================================================================== */

    async initializePanel(
      panel
    ) {
      if (!panel) {
        return;
      }

      const sourceType =
        panel.dataset.sourceType;

      await this.waitForNextFrame();


      /*
       * Collection
       */
      if (
        sourceType ===
        "collection_tab"
      ) {
        this.renderCollectionPanel(
          panel
        );
      }


      /*
       * Nosto
       */
      if (
        sourceType ===
          "nosto_tab" &&
        panel.dataset
          .recommendationsReady ===
          "true"
      ) {
        await this
          .renderStoredNostoPanel(
            panel
          );
      }


      /*
       * Slider externo
       */
      if (
        panel.dataset.rendered ===
        "true"
      ) {
        await this
          .activatePanelSlider(
            panel
          );
      }
    }


    /* ==========================================================================
       Nosto recommendations
       ========================================================================== */

    async handleRecommendationsLoaded(
      event
    ) {
      if (!event.detail) {
        return;
      }

      const recommendationsElement =
        event.target.closest(
          "nosto-recommendations"
        );

      const panel =
        event.target.closest(
          "[data-nosto-carousel-panel]"
        );

      if (
        !panel ||
        !this.contains(panel)
      ) {
        return;
      }


      /*
       * La misma estructura puede utilizarse
       * como carrusel o grid.
       */
      const isProductGrid =
        recommendationsElement
          ?.hasAttribute(
            "data-nosto-product-grid"
          );

      panel.dataset.nostoLayout =
        isProductGrid
          ? "grid"
          : "carousel";


      /*
       * No renderizamos dos veces
       * la misma respuesta.
       */
      if (
        panel.dataset.rendered ===
          "true" ||
        panel.dataset.rendering ===
          "true"
      ) {
        return;
      }


      let products =
        this.getRecommendedProducts(
          event.detail
        );


      /*
       * Respetamos el límite configurado
       * en el componente.
       */
      const limit =
        Number.parseInt(
          recommendationsElement
            ?.dataset.limit,
          10
        );

      if (
        Number.isFinite(limit) &&
        limit > 0
      ) {
        products =
          products.slice(
            0,
            limit
          );
      }


      if (!products.length) {
        this.setPanelEmpty(
          panel
        );

        return;
      }


      /*
       * Conservamos el contexto de Nosto.
       *
       * Este contexto pertenece a la capa
       * de recomendaciones/tracking,
       * no a ProductCard.js.
       */
      panel.nostoRecommendationContext = {
        placementId:
          String(
            event.detail
              .placementId || ""
          ).trim(),

        resultId:
          String(
            event.detail
              .resultId || ""
          ).trim(),

        divId:
          String(
            event.detail
              .divId || ""
          ).trim(),
      };


      /*
       * Guardamos los productos en el panel.
       *
       * Si el tab está oculto no hacemos todavía
       * las peticiones AJAX de product-card.
       */
      panel.nostoRecommendedProducts =
        products;

      panel.dataset
        .recommendationsReady =
        "true";


      if (
        panel.hidden ||
        !panel.classList.contains(
          "is-active"
        )
      ) {
        return;
      }


      await this
        .renderStoredNostoPanel(
          panel
        );


      /*
       * Las recomendaciones pudieron llegar
       * después del initializePanel inicial.
       */
      if (
        panel.dataset.rendered ===
        "true"
      ) {
        await this
          .activatePanelSlider(
            panel
          );
      }
    }


    handleRecommendationsError(
      event
    ) {
      const panel =
        event.target.closest(
          "[data-nosto-carousel-panel]"
        );

      if (
        !panel ||
        !this.contains(panel)
      ) {
        return;
      }

      panel.dataset.error =
        "true";

      const status =
        panel.querySelector(
          "[data-nosto-product-carousel-status]"
        );

      if (status) {
        status.textContent =
          "No fue posible cargar las recomendaciones.";
      }

      console.warn(
        "[NostoProductCarousel] Nosto no devolvió recomendaciones.",
        event.detail?.error
      );
    }


    async renderStoredNostoPanel(
      panel
    ) {
      if (
        !panel ||
        panel.dataset.rendered ===
          "true" ||
        panel.dataset.rendering ===
          "true"
      ) {
        return;
      }

      const products =
        panel.nostoRecommendedProducts;

      if (
        !Array.isArray(products) ||
        !products.length
      ) {
        return;
      }

      panel.dataset.rendering =
        "true";

      try {
        await this.renderNostoPanel(
          panel,
          products
        );

        panel.dataset.rendered =
          "true";
      } finally {
        delete panel.dataset
          .rendering;
      }
    }


    /* ==========================================================================
       Product normalization
       ========================================================================== */

    getRecommendedProducts(
      detail
    ) {
      const source =
        detail.allRecommendedProducts ??
        detail.recommendedProducts ??
        detail.products ??
        [];

      if (!Array.isArray(source)) {
        return [];
      }

      const uniqueProducts = [];

      const seenProducts =
        new Set();


      source.forEach(
        (product) => {
          const normalized =
            this.normalizeProduct(
              product
            );

          if (!normalized) {
            return;
          }

          const uniqueKey =
            normalized.handle ||
            normalized.productId;

          if (
            !uniqueKey ||
            seenProducts.has(
              uniqueKey
            )
          ) {
            return;
          }

          seenProducts.add(
            uniqueKey
          );

          uniqueProducts.push(
            normalized
          );
        }
      );


      this.recommendedProducts =
        uniqueProducts;

      return uniqueProducts;
    }


    normalizeProduct(
      product
    ) {
      if (!product) {
        return null;
      }


      /*
       * Algunas implementaciones pueden
       * entregar únicamente el ID.
       */
      if (
        typeof product ===
        "string"
      ) {
        const value =
          this.normalizeString(
            product
          );

        if (!value) {
          return null;
        }

        return {
          productId: value,
          handle: "",
          url: "",
          raw: product,
        };
      }


      const productId =
        this.normalizeString(
          product.productId ??
          product.product_id ??
          product.id
        );


      const url =
        this.normalizeString(
          product.url ??
          product.productUrl ??
          product.product_url
        );


      const handle =
        this.normalizeString(
          product.handle ??
          product.productHandle ??
          product.product_handle
        ) ||
        this.getHandleFromUrl(
          url
        );


      /*
       * ProductViewService necesita poder
       * identificar el producto por handle/url.
       */
      if (
        !productId &&
        !handle
      ) {
        return null;
      }


      return {
        productId,
        handle,
        url,
        raw: product,
      };
    }


    getHandleFromUrl(
      url
    ) {
      if (!url) {
        return "";
      }

      try {
        const parsedUrl =
          new URL(
            url,
            window.location.origin
          );

        const match =
          parsedUrl.pathname.match(
            /\/products\/([^/?#]+)/
          );

        return (
          match?.[1] || ""
        );
      } catch (error) {
        return "";
      }
    }


    /* ==========================================================================
       AJAX Product Card rendering
       ========================================================================== */

    async renderNostoPanel(
      panel,
      products
    ) {
      const track =
        panel.querySelector(
          "[data-nosto-product-carousel-track]"
        );

      if (!track) {
        return;
      }


      /*
       * Eliminamos skeletons iniciales.
       */
      track
        .querySelectorAll(
          "[data-nosto-carousel-skeleton]"
        )
        .forEach(
          (skeleton) => {
            skeleton.remove();
          }
        );


      /*
       * Creamos todos los slots primero.
       *
       * Esto conserva exactamente el orden
       * entregado por Nosto aunque las peticiones
       * AJAX terminen en distinto orden.
       */
      const placeholders =
        products.map(
          (
            product,
            index
          ) => {
            const placeholder =
              document.createElement(
                "div"
              );

            placeholder.className =
              "nosto-product-carousel__item";

            placeholder.dataset.gridItem =
              "";

            placeholder.dataset
              .nostoProductCarouselItem =
              "";

            placeholder.dataset
              .productIndex =
              String(index);

            placeholder.dataset
              .productId =
              product.productId || "";

            placeholder.dataset
              .productHandle =
              product.handle || "";

            placeholder.dataset
              .productUrl =
              product.url || "";

            placeholder.setAttribute(
              "role",
              "listitem"
            );


            placeholder.innerHTML = `
              <div class="nosto-product-carousel__item-loading">
                <div class="nosto-product-carousel__skeleton-media"></div>
                <div class="nosto-product-carousel__skeleton-line"></div>
                <div class="nosto-product-carousel__skeleton-line nosto-product-carousel__skeleton-line--short"></div>
              </div>
            `;


            track.appendChild(
              placeholder
            );


            return {
              ...product,
              placeholder,
            };
          }
        );


      /*
       * El propio ProductViewService ya controla
       * también concurrencia, caché y deduplicación.
       *
       * Conservamos 4 workers aquí para no inundar
       * renderProduct con tareas simultáneas.
       */
      await this.runWithConcurrency(
        placeholders,
        4,
        async (product) => {
          await this.renderProduct(
            product,
            product.placeholder
          );
        }
      );


      this.cleanupFailedItems(
        panel
      );
    }


    async renderProduct(
      product,
      placeholder
    ) {
      if (
        this.destroyed ||
        !placeholder?.isConnected
      ) {
        return null;
      }


      try {
        const panel =
          placeholder.closest(
            "[data-nosto-carousel-panel]"
          );

        if (!panel) {
          throw new Error(
            "No se encontró el panel del producto."
          );
        }


        const recommendationContext =
          panel
            .nostoRecommendationContext ||
          {};


        const attributionKey =
          String(
            recommendationContext
              .resultId || ""
          ).trim();


        /*
         * Solicitar siempre la nueva
         * product view universal.
         */
        const html =
          await this
            .productViewService
            .getCard({
              handle:
                product.handle || "",

              url:
                product.url || "",

              productId:
                product.productId || "",

              view:
                "product-card",

              context: {
                source:
                  "carousel-recommendation",

                track:
                  "recommendation",

                attributionKey,

                placementId:
                  recommendationContext
                    .placementId || "",

                resultId:
                  recommendationContext
                    .resultId || "",

                divId:
                  recommendationContext
                    .divId || "",
              },
            });


        /*
         * El componente pudo haberse desmontado
         * mientras la petición estaba pendiente.
         */
        if (
          this.destroyed ||
          !placeholder.isConnected
        ) {
          return null;
        }


        /*
         * Convertimos el HTML en DOM sin insertar
         * wrappers adicionales.
         */
        const template =
          document.createElement(
            "template"
          );

        template.innerHTML =
          html.trim();


        const cardRoot =
          template.content
            .querySelector(
              "[data-product-card]"
            );


        if (!cardRoot) {
          throw new Error(
            'La vista "product-card" no devolvió [data-product-card].'
          );
        }


        /*
         * ================================================================
         * Contexto de Nosto
         * ================================================================
         *
         * ProductCard.js no usa estos atributos.
         *
         * Quedan disponibles para la capa de
         * attribution/tracking.
         */

        placeholder.dataset
          .productSource =
          "nosto-recommendation";

        placeholder.dataset
          .nostoPlacementId =
          recommendationContext
            .placementId || "";

        placeholder.dataset
          .nostoResultId =
          recommendationContext
            .resultId || "";

        placeholder.dataset
          .nostoDivId =
          recommendationContext
            .divId || "";

        placeholder.dataset
          .nostoAttributionKey =
          attributionKey;


        /*
         * También los dejamos sobre product-card.
         *
         * Esto permite localizar el contexto incluso
         * si posteriormente alguna integración trabaja
         * directamente con el card.
         */
        cardRoot.dataset
          .productSource =
          "nosto-recommendation";

        cardRoot.dataset
          .nostoPlacementId =
          recommendationContext
            .placementId || "";

        cardRoot.dataset
          .nostoResultId =
          recommendationContext
            .resultId || "";

        cardRoot.dataset
          .nostoDivId =
          recommendationContext
            .divId || "";

        cardRoot.dataset
          .nostoAttributionKey =
          attributionKey;


        /*
         * Sustituimos el skeleton por
         * el nuevo <product-card>.
         */
        placeholder.replaceChildren(
          cardRoot
        );


        placeholder.classList.add(
          "is-loaded"
        );


        /*
         * ProductCard.js tiene MutationObserver,
         * pero inicializamos explícitamente para
         * garantizar que el card esté preparado
         * antes del slider exterior.
         */
        if (
          typeof window
            .initializeProductCards ===
          "function"
        ) {
          window.initializeProductCards(
            placeholder
          );
        }


        return cardRoot;

      } catch (error) {
        console.error(
          "[NostoProductCarousel] No se pudo renderizar el producto.",
          {
            product,
            error,
          }
        );


        /*
         * Un producto que falló no debe dejar
         * un hueco vacío en el carrusel.
         */
        placeholder.remove();


        return null;
      }
    }


    /* ==========================================================================
       Failed / empty items
       ========================================================================== */

    cleanupFailedItems(
      panel
    ) {
      if (!panel) {
        return;
      }


      panel
        .querySelectorAll(
          "[data-nosto-product-carousel-item]"
        )
        .forEach(
          (item) => {
            if (
              !item.querySelector(
                "[data-product-card]"
              )
            ) {
              item.remove();
            }
          }
        );


      const cards =
        panel.querySelectorAll(
          "[data-product-card]"
        );


      if (!cards.length) {
        this.setPanelEmpty(
          panel
        );
      }
    }


    setPanelEmpty(
      panel
    ) {
      panel.dataset.empty =
        "true";
    }


    /* ==========================================================================
       Tabs
       ========================================================================== */

    handleTabClick(
      event
    ) {
      const tab =
        event.target.closest(
          "[data-nosto-carousel-tab]"
        );

      if (
        !tab ||
        !this.contains(tab)
      ) {
        return;
      }

      event.preventDefault();


      const panelId =
        tab.dataset.panelId;

      if (!panelId) {
        return;
      }


      this.activateTab(
        tab,
        panelId
      );
    }


    async activateTab(
      selectedTab,
      panelId
    ) {
      if (
        this.dataset.changingTab ===
        "true"
      ) {
        return;
      }


      this.dataset.changingTab =
        "true";


      try {
        const tabs =
          this.querySelectorAll(
            "[data-nosto-carousel-tab]"
          );

        const panels =
          this.querySelectorAll(
            "[data-nosto-carousel-panel]"
          );


        tabs.forEach(
          (tab) => {
            const isSelected =
              tab ===
              selectedTab;

            tab.classList.toggle(
              "is-active",
              isSelected
            );

            tab.setAttribute(
              "aria-selected",
              String(
                isSelected
              )
            );
          }
        );


        let activePanel =
          null;


        panels.forEach(
          (panel) => {
            const isSelected =
              panel.id ===
              panelId;

            panel.hidden =
              !isSelected;

            panel.classList.toggle(
              "is-active",
              isSelected
            );

            if (isSelected) {
              activePanel =
                panel;
            }
          }
        );


        if (!activePanel) {
          return;
        }


        await this.waitForNextFrame();

        await this.initializePanel(
          activePanel
        );

      } finally {
        delete this.dataset
          .changingTab;
      }
    }


    /* ==========================================================================
       External carousel
       ========================================================================== */

    async activatePanelSlider(
      panel
    ) {
      if (
        !panel ||
        panel.hidden ||
        !panel.classList.contains(
          "is-active"
        ) ||
        panel.dataset.rendered !==
          "true"
      ) {
        return;
      }


      /*
       * Nosto puede indicar explícitamente
       * que esta vista debe permanecer como grid.
       */
      if (
        panel.dataset.nostoLayout ===
        "grid"
      ) {
        /*
         * Aunque no exista slider externo,
         * refrescamos ProductCard.
         */
        if (
          typeof window
            .refreshProductCards ===
          "function"
        ) {
          window.refreshProductCards(
            panel
          );
        }

        return;
      }


      const gridSlider =
        panel.querySelector(
          "[data-nosto-product-carousel-slider]"
        );

      const track =
        panel.querySelector(
          "[data-nosto-product-carousel-track]"
        );


      if (
        !gridSlider ||
        !track
      ) {
        return;
      }


      /*
       * Esperamos a que el contenido AJAX
       * tenga dimensiones reales.
       */
      await this.waitForNextFrame();


      /*
       * Si no existe overflow no necesitamos
       * mantener activo el slider exterior.
       */
      if (
        track.clientWidth <= 0 ||
        track.scrollWidth <=
          track.clientWidth + 1
      ) {
        gridSlider.destroy?.();


        if (
          typeof window
            .refreshProductCards ===
          "function"
        ) {
          window.refreshProductCards(
            panel
          );
        }


        return;
      }


      /*
       * El grid-slider nativo necesita conocer
       * nuevamente las slides porque los cards
       * fueron insertados por AJAX.
       */
      gridSlider.getSlidesWidth =
        () =>
          track.scrollWidth;

      gridSlider.slider =
        track;

      gridSlider.slides =
        track.querySelectorAll(
          ":scope > [data-grid-item]"
        );


      /*
       * No reinicializamos si ya existe.
       *
       * Evita duplicar:
       * - flechas
       * - observers
       * - listeners
       * - drag
       */
      if (
        !gridSlider.isInitialized
      ) {
        gridSlider.destroy?.();

        await this.waitForNextFrame();


        gridSlider.slider =
          track;

        gridSlider.slides =
          track.querySelectorAll(
            ":scope > [data-grid-item]"
          );

        gridSlider.getSlidesWidth =
          () =>
            track.scrollWidth;


        if (
          typeof gridSlider.init ===
          "function"
        ) {
          gridSlider.init();
        } else {
          gridSlider.dispatchEvent(
            new CustomEvent(
              "theme:grid-slider:init",
              {
                bubbles: false,
              }
            )
          );
        }

      } else {
        gridSlider.toggleSlider?.();
      }


      /*
       * ProductCard.js maneja Flickity,
       * hover, dots, swatches, etc.
       *
       * Después de activar/mostrar un tab,
       * actualizamos sus dimensiones internas.
       */
      if (
        typeof window
          .refreshProductCards ===
        "function"
      ) {
        window.refreshProductCards(
          panel
        );
      }
    }


    /* ==========================================================================
       Images
       ========================================================================== */

    async waitForPanelImages(
      panel
    ) {
      const images =
        Array.from(
          panel.querySelectorAll(
            "img"
          )
        );


      await Promise.all(
        images.map(
          (image) => {
            if (image.complete) {
              return Promise.resolve();
            }


            return new Promise(
              (resolve) => {
                image.addEventListener(
                  "load",
                  resolve,
                  {
                    once: true,
                  }
                );

                image.addEventListener(
                  "error",
                  resolve,
                  {
                    once: true,
                  }
                );
              }
            );
          }
        )
      );
    }


    /* ==========================================================================
       Concurrency
       ========================================================================== */

    async runWithConcurrency(
      items,
      concurrency,
      worker
    ) {
      let currentIndex = 0;


      const runners =
        Array.from(
          {
            length:
              Math.min(
                concurrency,
                items.length
              ),
          },

          async () => {
            while (
              currentIndex <
              items.length
            ) {
              const itemIndex =
                currentIndex++;

              await worker(
                items[itemIndex],
                itemIndex
              );
            }
          }
        );


      await Promise.all(
        runners
      );
    }


    /* ==========================================================================
       Utils
       ========================================================================== */

    normalizeString(
      value
    ) {
      if (
        value === null ||
        value === undefined
      ) {
        return "";
      }

      return String(
        value
      ).trim();
    }


    waitForNextFrame() {
      return new Promise(
        (resolve) => {
          requestAnimationFrame(
            () => {
              requestAnimationFrame(
                resolve
              );
            }
          );
        }
      );
    }
  }


  /* ==========================================================================
     Custom Element
     ========================================================================== */

  if (
    !customElements.get(
      "nosto-product-carousel"
    )
  ) {
    customElements.define(
      "nosto-product-carousel",
      NostoProductCarousel
    );
  }
})();